import React, { useEffect, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Image,
  Text,
  Box,
  Button,
} from "@chakra-ui/react";
import { FaDownload } from "react-icons/fa";
import type { Media } from "@twilio/conversations";

interface ImagePreviewModalProps {
  isOpen: boolean;
  media: Media | null;
  imageBlob: Blob | null;
  onClose: () => void;
  onDownload?: (media: Media) => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  media,
  imageBlob,
  onClose,
  onDownload,
}) => {
  const imageUrl = useMemo(
    () => (isOpen && imageBlob ? URL.createObjectURL(imageBlob) : null),
    [isOpen, imageBlob]
  );
  useEffect(
    () => () => imageUrl ? URL.revokeObjectURL(imageUrl) : undefined,
    [imageUrl]
  );

  if (!isOpen || !media || !imageBlob) return null;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxW="min(90vw, 800px)"
        maxH="min(90vh, 800px)"
        w="min(90vw, 800px)"
        h="min(90vh, 800px)"
        borderRadius="lg"
        overflow="hidden"
        bg="white"
      >
        <Box position="absolute" top={2} right={12} zIndex={10}>
          {onDownload && media && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onDownload(media)}
              _hover={{ bg: "blue.50" }}
              disabled={!media}
            >
              <FaDownload />
            </Button>
          )}
        </Box>

        <ModalCloseButton
          position="absolute"
          top={2}
          right={3}
          zIndex={10}
          bg="white"
          borderRadius="full"
          color="black"
          _hover={{
            bg: "gray.200",
          }}
        />

        {/* Image Content */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          w="100%"
          h="100%"
          bg="transparent"
          p={4}
          py={10}
        >
          <Image
            src={imageUrl || ""}
            alt={media.filename || "Image"}
            maxW="100%"
            maxH="100%"
            objectFit="contain"
            borderRadius="lg"
          />
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default ImagePreviewModal;
